import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminFeedback, AdminReflection } from '@/types/admin'

export default function AdminContentPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [feedback, setFeedback] = useState<AdminFeedback[]>([])
  const [reflections, setReflections] = useState<AdminReflection[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [feedbackRes, reflectionsRes] = await Promise.all([
          adminApi.contentFeedback(),
          adminApi.contentReflections(),
        ])
        setFeedback((feedbackRes as { data?: AdminFeedback[] }).data || (feedbackRes as unknown as AdminFeedback[]))
        setReflections(
          (reflectionsRes as { data?: AdminReflection[] }).data || (reflectionsRes as unknown as AdminReflection[])
        )
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load content')
      }
    }
    void load()
  }, [])

  const handleDeleteReflection = async (id: string) => {
    try {
      await adminApi.deleteReflection(id)
      setReflections((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      setError((err as Error)?.message || 'Unable to delete reflection')
    }
  }

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Content'
          infoText='Moderate feedback and reflections via admin content endpoints.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3'>
          <h3 className='text-lg font-semibold text-gray-900'>Couple Feedback</h3>
          <div className='space-y-3'>
            {feedback.length === 0 && (
              <p className='text-sm text-gray-600'>No feedback available.</p>
            )}
            {feedback.map((item) => (
              <div key={item.id} className='p-3 rounded-lg bg-gray-50 text-sm'>
                <p className='font-semibold text-gray-900'>{item.id}</p>
                <p className='text-gray-700'>{item.message}</p>
                <div className='mt-2 flex gap-2 text-xs'>
                  <button className='px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200'>Keep</button>
                  <button className='px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100'>Flag</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-3'>
          <h3 className='text-lg font-semibold text-gray-900'>Video Reflections</h3>
          <div className='space-y-3'>
            {reflections.length === 0 && (
              <p className='text-sm text-gray-600'>No reflections available.</p>
            )}
            {reflections.map((item) => (
              <div key={item.id} className='p-3 rounded-lg bg-gray-50 text-sm'>
                <p className='font-semibold text-gray-900'>{item.id}</p>
                <p className='text-gray-700'>{item.message}</p>
                <div className='mt-2 flex gap-2 text-xs'>
                  <button className='px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200'>Keep</button>
                  <button className='px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100'>
                    <span role='button' onClick={() => void handleDeleteReflection(item.id)}>
                      Delete
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  )
}
